<script setup>
import { ref, watch, onMounted } from "vue";
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import Swal from 'sweetalert2';
import { ResourceClient } from "@/services/ResourceClient";
import { LessonsClient } from "@/services/LessonsClient";
import LessonVideoTab from "./LessonVideoTab.vue";
import LessonResourcesTab from "./LessonResourcesTab.vue";
const resourceClient = new ResourceClient();
const lessonsClient = new LessonsClient();

const props = defineProps({
  lessonId: {
    type: Number,
    default: null
  },
  chapterId: {
    type: Number,
    default: null
  },
  existingLesson: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['save-lesson', 'delete-lesson']);

// Reactive data
const lessonData = ref({
  id: null,
  title: "",
  orden: 0,
  contenido_texto: ""
});
const files = ref([]);
const isLoading = ref(false);

// Initialize component
if (props.existingLesson) {
  lessonData.value = {
    ...props.existingLesson
  };
}

// Lesson methods
const updateLesson = async () => {
  try {
    isLoading.value = true;

    const updateData = {
      title: lessonData.value.titulo,
      order: lessonData.value.orden,
      contenido_texto: lessonData.value.contenido_texto
    };

    await lessonsClient.updateLesson(props.lessonId, updateData);
    emit('save-lesson', props.lessonId, updateData);
    Swal.fire('¡Guardado!', 'La leccion ha sido actualizada', 'success');

  } catch (error) {
    console.error("Error updating lesson:", error);
    Swal.fire('Error', 'No se pudo actualizar la leccion', 'error');
  } finally {
    isLoading.value = false;
  }
};

const deleteLesson = async () => {
  const result = await Swal.fire({
  title: '¿Eliminar permanentemente?',
  text: "Todos los datos asociados serán eliminados.",
  icon: 'warning',
  showCancelButton: true,
  confirmButtonColor: '#dc3545', // Red
  cancelButtonColor: '#6c757d',  // Gray
  confirmButtonText: 'Eliminar',
  cancelButtonText: 'Cancelar',
  buttonsStyling: false,
  customClass: {
    actions: 'grid grid-cols-2 gap-2 w-full', // Key change for 2 columns
    confirmButton: 'btn btn-danger w-full',
    cancelButton: 'btn btn-secondary w-full'
  },
  reverseButtons: false // Keep original order
});

  if (result.isConfirmed) {
    try {
      await lessonsClient.deleteLesson(props.lessonId)  .catch(e => {
          if (!e.message.includes('JSON')) throw e; // Ignore JSON parse errors
        });
      emit('delete-lesson', {
        chapterId: props.chapterId,
        lessonId: props.lessonId
      });
    } catch (error) {
      console.error("Error deleting lesson:", error);
      Swal.fire('Error', 'No se pudo eliminar la lección', 'error');
    }
  }
};
</script>

<template>
  <div class="card card2">
    <div class="card-header d-flex pb-4 flex-column flex-md-row">
      <!-- Column 1 (Title) -->
      <div class="col-12 col-md-8">
        <h6 class="font-weight-bolder mb-0">
          <i class="fas fa-book pe-2"></i> {{ lessonData.titulo || 'Nueva Leccion' }}
        </h6>
        <p class="text-sm mb-1">
          Edita información y asigna recursos a esta lección
        </p>
      </div>

      <!-- Column 2 (Button) -->
      <div class="col-12 col-md-4 gap-2 d-flex align-items-center justify-content-between">
        <button class="mt-2 mb-0 ms-2 btn btn-outline-dark w-70 mt-lg-0" @click="updateLesson" :disabled="isLoading">
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="far fa-save pe-2"></i>
          Guardar
      </button>
        <button type="button" class="btn-sm mt-2 ms-0 px-4 mb-0 btn btn-dark mt-lg-0" @click="deleteLesson(props.lessonId)"
          :disabled="!lessonId">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>

    <ul class="nav nav-tabs text-xs px-1 d-flex justify-content-center" id="courseTabs" role="tablist">
      <li class="nav-item " role="presentation">
        <button class="nav-link px-3 px-lg-4 active " id="info-tab" data-bs-toggle="tab" data-bs-target="#info"
          type="button" role="tab"><i class="fas fa-align-left pe-2"></i>Información</button>
      </li>
      <li class="nav-item" role="presentation">
        <button class="nav-link px-4 px-lg-5" id="video-tab" data-bs-toggle="tab" data-bs-target="#video" type="button"
          role="tab"><i class="fab fa-youtube pe-2"></i>Video</button>
      </li>
      <li class="nav-item" role="presentation">
        <button class="nav-link px-4 px-lg-5" id="resources-tab" data-bs-toggle="tab" data-bs-target="#resources"
          type="button" role="tab"><i class="fas fa-paperclip pe-2"></i>Recursos</button>
      </li>
    </ul>

    <div class="tab-content " id="courseTabsContent">
      <div class="tab-pane fade show active" id="info" role="tabpanel">
        <div class="mt-4 row ">
          <div class="col-lg-12">
            <div class="card-body pt-0">
              <!-- Columna de Información del Producto -->
              <h6 class="font-weight-bolder"><i class="fas fa-align-left pe-2"></i> Información de la Leccion</h6>
              <div class="col-12">
                <div class="d-flex justify-content-between align-items-center">
                  <label>Título <span class="text-danger">*</span></label>
                  <small class="text-muted ms-4">(máx. 100 caracteres)</small>
                </div>
              

                <input class="form-control" type="text" placeholder="Lección" 
                maxlength="100" 
                v-model="lessonData.titulo" />

                <label class="mt-4">Objetivos</label>
                <div id="edit-lesson-description-edit">
                  <QuillEditor theme="snow" v-model:content="lessonData.contenido_texto" content-type="html" :options="{
                    modules: {
                      toolbar: [
                        ['bold', 'italic', 'underline'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                      ]
                    }
                  }" />
                </div>
              </div>          
            </div>
          </div>
        </div>
      </div>

      <div class="tab-pane fade" id="video" role="tabpanel">
        <LessonVideoTab 
          :files="files" 
          :lessonId="lessonId"
          @resource-updated="fetchLessonResources"
        />
      </div>

      <div class="tab-pane fade" id="resources" role="tabpanel">
        <LessonResourcesTab 
          :files="files" 
          :lessonId="lessonId"
          @resource-updated="fetchLessonResources"
        />
      </div>
    </div>
  </div>
</template>

<style>
.card2 {
  min-height: 800px;
}

#edit-lesson-description-edit .ql-container {
  min-height: 250px;
  max-height: 400px;
  overflow-y: auto;
}
.spinner-border {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}
</style>